import { ChevronRight, Folder, FolderOpen, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { Document, DocumentFolder } from '@/types/document';

export type { DocumentFolder, Document as DocumentItem };

interface FolderTreeProps {
  folders: DocumentFolder[];
  onDocumentClick?: (document: Document) => void;
  onDeleteDocument?: (document: Document) => void;
  className?: string;
}

export function DocumentFolderTree({
  folders,
  onDocumentClick,
  onDeleteDocument,
  className,
}: FolderTreeProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const getDocumentUrl = (document: Document): string | null => {
    if (document.localFilePath) {
      return `/api/v1/documents/${document.employeeId}/${document.id}`;
    }
    if (document.fileUrl) {
      return document.fileUrl;
    }
    if (document.url) {
      return document.url;
    }
    return null;
  };

  const getFileIcon = (mimeType?: string | null, type?: string) => {
    const fileType = mimeType?.split('/')[0] || type?.toLowerCase() || 'file';

    if (fileType === 'image') {
      return '🖼️';
    }
    if (fileType === 'application' || type === 'pdf') {
      return '📄';
    }
    if (type === 'doc' || type === 'docx') {
      return '📝';
    }
    if (type === 'xls' || type === 'xlsx') {
      return '📊';
    }
    return '📎';
  };

  const formatFileSize = (bytes?: number | null): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (folders.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No folders found
      </div>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      {folders.map((folder) => {
        const isExpanded = expandedFolders.has(folder.id);
        const hasDocuments = folder.documents.length > 0;

        return (
          <div key={folder.id} className="select-none">
            {/* Folder Header */}
            <div
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                'hover:bg-muted/50 cursor-pointer',
                isExpanded && 'bg-muted/30'
              )}
              onClick={() => toggleFolder(folder.id)}
            >
              <ChevronRight
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  isExpanded && 'rotate-90'
                )}
              />
              {isExpanded ? (
                <FolderOpen className="h-4 w-4 text-blue-500" />
              ) : (
                <Folder className="h-4 w-4 text-blue-500" />
              )}
              <span className="flex-1 font-medium">{folder.name}</span>
              {hasDocuments && (
                <span className="text-xs text-muted-foreground">
                  {folder.documents.length}
                </span>
              )}
            </div>

            {/* Folder Description */}
            {folder.description && (
              <div className="ml-9 mb-1 text-xs text-muted-foreground">
                {folder.description}
              </div>
            )}

            {/* Documents List */}
            {isExpanded && hasDocuments && (
              <div className="ml-6 mt-1 space-y-0.5 border-l-2 border-muted pl-3">
                {folder.documents.map((document) => {
                  const documentUrl = getDocumentUrl(document);
                  const canOpen = !!documentUrl;

                  return (
                    <div
                      key={document.id}
                      className={cn(
                        'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                        canOpen
                          ? 'cursor-pointer hover:bg-muted/50'
                          : 'cursor-not-allowed opacity-60'
                      )}
                      onClick={() => {
                        if (canOpen && onDocumentClick) {
                          onDocumentClick(document);
                        } else if (canOpen && documentUrl) {
                          window.open(documentUrl, '_blank');
                        }
                      }}
                    >
                      <span className="text-base">
                        {getFileIcon(document.mimeType, document.type)}
                      </span>
                      <span className="flex-1 truncate">{document.name}</span>
                      {document.fileSize && (
                        <span className="text-xs text-muted-foreground">
                          {formatFileSize(document.fileSize)}
                        </span>
                      )}
                      {document.uploadedAt && (
                        <span className="text-xs text-muted-foreground">
                          {new Date(document.uploadedAt).toLocaleDateString()}
                        </span>
                      )}
                      {onDeleteDocument && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteDocument(document);
                          }}
                          className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                          aria-label={`Delete ${document.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty folder message */}
            {isExpanded && !hasDocuments && (
              <div className="ml-9 mt-1 text-xs text-muted-foreground italic">
                No documents in this folder
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

